import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminGetTickets } from "../../services/ticketService.js";
import {
  health, deviceStatus, serverStatus, byUrgency, countStatuses, buildIncidents,
  backupSummary, timeAgo, timeAgoEpoch, storageColor, diskInfo, formatStorage,
  TICKET_STATUS_LABEL, TICKET_PRIORITY_LABEL, TICKET_ACTIVE, ticketLevel, ticketSla,
  REFRESH_INTERVAL_MS,
} from "./dashboardHelpers.js";
import { Section, KpiTile, LiveIndicator, StatusDot, Facts } from "./DashboardParts.jsx";
import { BackupJobList, LastBackupLine } from "./DeviceCards.jsx";

// ══════════════════════════════════════════════════════
//  Onglet "Vue colonnes" — tout sur une seule page :
//  Tickets | UDM | NAS | Serveurs, une ligne compacte par élément.
//  Les éléments en problème remontent en premier avec la raison écrite ;
//  un clic sur une ligne la déplie. Aucune limite : tout est affiché.
// ══════════════════════════════════════════════════════

const LEVEL_RANK = (s) => health(s).rank;

// ── Ligne compacte dépliable ─────────────────────────
function CompactRow({ status, name, sub, side, why, expandAll, children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(expandAll); }, [expandAll]);
  const h = health(status);
  const toggle = () => setOpen(o => !o);
  return (
    <div
      role="button" tabIndex={0} aria-expanded={open}
      onClick={toggle}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
      style={{
        background: status === "CRITICAL" ? h.bg : "var(--card-bg)", cursor: "pointer", minWidth: 0,
        border: "1px solid var(--border)", borderLeft: `4px solid ${status === "OK" ? "var(--success)" : h.color}`,
        borderRadius: 9, padding: "6px 9px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <StatusDot status={status} size={8} />
        <span style={{ fontWeight: 700, fontSize: 12.5, color: "var(--text-1)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>{side}</span>
      </div>
      <div style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
      {why && <div style={{ fontSize: 11, fontWeight: 600, marginLeft: 15, marginTop: 1, color: h.color }}>{why}</div>}
      {open && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 7, paddingTop: 7, borderTop: "1px solid var(--border)", cursor: "default" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function MiniBar({ pct, color }) {
  return (
    <>
      <span style={{ width: 34, height: 4, borderRadius: 2, background: "var(--navy-soft)", overflow: "hidden", display: "inline-block" }}>
        <i style={{ display: "block", height: "100%", width: `${Math.min(100, pct)}%`, background: color }} />
      </span>
      <span style={{ color }}>{pct} %</span>
    </>
  );
}

// ── Colonne ──────────────────────────────────────────
function Column({ icon, title, total, counts, labels, extra, children }) {
  const chip = (n, label, color) => (
    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: "1px 7px", border: "1px solid var(--border)", background: "var(--card-bg)", color }}>{n} {label}</span>
  );
  return (
    <section style={{ background: "var(--navy-soft)", border: "1px solid var(--border)", borderRadius: 13, minWidth: 0 }}>
      <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <i className={`bi ${icon}`} style={{ color: "var(--text-3)" }} />
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: "var(--text-1)" }}>{title}</h2>
          <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "var(--text-3)" }}>{total}</span>
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
          {chip(counts.OK, labels[0], "var(--success)")}
          {chip(counts.WARNING, labels[1], "var(--warning)")}
          {chip(counts.CRITICAL, labels[2], "var(--danger)")}
        </div>
        {extra}
      </div>
      <div style={{ padding: 6, display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </section>
  );
}

// ── Données tickets (rafraîchies avec le reste) ──────
export function useTicketList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const res = await adminGetTickets();
      setTickets(Array.isArray(res) ? res : res?.data ?? []);
      setError("");
    } catch (err) {
      setError(err.message || "Erreur de chargement des tickets");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);
  return { tickets, loading, error, reload: load };
}

const TICKET_FILTERS = [
  ["actifs", "Actifs"], ["resolus", "Résolus"], ["fermes", "Fermés"], ["tous", "Tous"],
];
const inFilter = (t, f) =>
  f === "tous" ? true :
  f === "actifs" ? TICKET_ACTIVE.includes(t.status) :
  f === "resolus" ? t.status === "RESOLVED" : t.status === "CLOSED";

// Boutons segmentés (partagés avec l'onglet Tour de contrôle)
export const segStyle = { display: "flex", background: "var(--navy-soft)", border: "1px solid var(--border)", borderRadius: 9, padding: 2, width: "fit-content" };
export const segBtn = (active) => ({
  font: "inherit", fontSize: 11.5, fontWeight: 600, border: 0, cursor: "pointer", padding: "4px 10px", borderRadius: 7,
  background: active ? "var(--card-bg)" : "transparent", color: active ? "var(--text-1)" : "var(--text-2)",
  boxShadow: active ? "0 1px 2px rgba(0,0,0,.12)" : "none",
});

// ── Onglet ───────────────────────────────────────────
/** Les 4 colonnes Tickets | UDM | NAS | Serveurs (utilisées par l'onglet Tour de contrôle). */
export default function ColumnsBoard({ data, tk, expandAll }) {
  const navigate = useNavigate();
  const { nas, gateways, servers, loading, errors } = data;
  const [filter, setFilter] = useState("actifs");
  const [showAllTickets, setShowAllTickets] = useState(false);
  const btn = segBtn, seg = segStyle;

  // Raisons affichées sous le nom (issues des incidents calculés)
  const why = useMemo(() => {
    const map = new Map();
    buildIncidents({ nas, gateways, servers }).forEach(i => {
      const k = `${i.kind}:${i.id}`;
      map.set(k, [...(map.get(k) || []), i.title]);
    });
    return (kind, id) => {
      const l = map.get(`${kind}:${id}`) || [];
      return l.length ? l[0] + (l.length > 1 ? ` (+${l.length - 1})` : "") : "";
    };
  }, [nas, gateways, servers]);

  const gwSorted  = useMemo(() => [...gateways].sort(byUrgency(deviceStatus, g => g.name)), [gateways]);
  const nasSorted = useMemo(() => [...nas].sort(byUrgency(deviceStatus, n => n.name)), [nas]);
  const srvSorted = useMemo(() => [...servers].sort(byUrgency(serverStatus, s => s.hostname)), [servers]);

  const ticketsView = useMemo(() => tk.tickets
    .filter(t => inFilter(t, filter))
    .sort((a, b) => LEVEL_RANK(ticketLevel(a)) - LEVEL_RANK(ticketLevel(b))
      || new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
  [tk.tickets, filter]);

  // La colonne Tickets s'aligne sur la hauteur de la plus longue des autres colonnes
  // (au moins 5 lignes) pour que la page tienne sans défiler ; « Voir plus » déroule le reste.
  const ticketLimit = Math.max(5, gateways.length, nas.length, servers.length);
  const ticketsShown = showAllTickets ? ticketsView : ticketsView.slice(0, ticketLimit);
  const ticketsHidden = ticketsView.length - ticketsShown.length;

  const cGw  = countStatuses(gateways.map(deviceStatus));
  const cNas = countStatuses(nas.map(deviceStatus));
  const cSrv = countStatuses(servers.map(serverStatus));
  const cTk  = countStatuses(ticketsView.map(ticketLevel));
  const countOf = (f) => tk.tickets.filter(t => inFilter(t, f)).length;

  return (
    <div>
      <div className="dash-cols">
        {/* ── Tickets ── */}
        <Column icon="bi-ticket-detailed" title="Tickets" total={ticketsView.length} counts={cTk}
          labels={["normaux", "hauts", "critiques"]}
          extra={
            <div style={{ ...seg, marginTop: 7 }} role="group" aria-label="Filtre tickets">
              {TICKET_FILTERS.map(([k, l]) => (
                <button key={k} style={btn(filter === k)} aria-pressed={filter === k} onClick={() => { setFilter(k); setShowAllTickets(false); }}>{l} {countOf(k)}</button>
              ))}
            </div>
          }>
          <Section loading={tk.loading} error={tk.error} count={ticketsView.length} empty="Aucun ticket dans ce filtre.">
            {ticketsShown.map(t => {
              const level = ticketLevel(t);
              const sla = ticketSla(t);
              return (
                <CompactRow key={t.id} status={level} expandAll={expandAll}
                  name={<><span style={{ color: "var(--text-3)", fontWeight: 600, fontFamily: "monospace" }}>{t.ateraTicketId ? `#${t.ateraTicketId}` : `#${String(t.id).substring(0, 6)}`}</span> {t.title}</>}
                  sub={[t.company?.name, TICKET_STATUS_LABEL[t.status] || t.status, t.systemAlert && "alerte système"].filter(Boolean).join(" · ")}
                  side={<span style={{ color: "var(--text-3)" }}>{t.createdAt ? timeAgo(t.createdAt) : ""}</span>}
                  why={sla === "late" ? "Délai de réponse dépassé" : ""}>
                  <Facts bordered={false} items={[
                    ["Client", t.company?.name],
                    ["Priorité", TICKET_PRIORITY_LABEL[t.priority] || t.priority],
                    ["Statut", TICKET_STATUS_LABEL[t.status] || t.status],
                    ["Catégorie", t.category],
                    ["Créé le", t.createdAt ? new Date(t.createdAt).toLocaleString("fr-FR") : "—"],
                  ]} />
                  <button className="btn-itm btn-itm-outline" style={{ padding: "3px 10px", fontSize: 11, marginTop: 6 }} onClick={() => navigate(`/tickets/${t.id}`)}>Ouvrir le ticket →</button>
                </CompactRow>
              );
            })}
            {(ticketsHidden > 0 || (showAllTickets && ticketsView.length > ticketLimit)) && (
              <button type="button" onClick={() => setShowAllTickets(v => !v)} style={{
                font: "inherit", width: "100%", marginTop: 6, padding: "7px 10px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", color: "var(--accent)", background: "var(--navy-soft)",
                border: "1px dashed var(--border)", borderRadius: 8,
              }}>
                {showAllTickets ? "Réduire ▲" : `Voir plus — ${ticketsHidden} ticket${ticketsHidden > 1 ? "s" : ""} de plus ▼`}
              </button>
            )}
          </Section>
        </Column>

        {/* ── UDM ── */}
        <Column icon="bi-router" title="UDM" total={gateways.length} counts={cGw} labels={["OK", "avert.", "crit."]}>
          <Section loading={loading} error={errors.monitoring} count={gwSorted.length} empty="Aucune passerelle enregistrée.">
            {gwSorted.map(g => {
              const st = deviceStatus(g);
              const off = st === "CRITICAL";
              return (
                <CompactRow key={g.id} status={st} expandAll={expandAll} name={g.name}
                  sub={[g.companyName, g.ispName && `FAI ${g.ispName}`].filter(Boolean).join(" · ")}
                  side={off ? <span style={{ color: "var(--danger)" }}>Hors ligne</span> : <span style={{ color: "var(--text-1)" }}>{g.latencyMs != null ? `${g.latencyMs} ms` : "—"}</span>}
                  why={why("udm", g.id)}>
                  <Facts bordered={false} items={[
                    ["WAN", g.wanStatus || "—", off ? "var(--danger)" : undefined],
                    ["Latence", g.latencyMs != null ? `${g.latencyMs} ms` : "—"],
                    ["Clients", g.connectedClients ?? "—"],
                    ["↓ / ↑", g.downMbps != null ? `${g.downMbps} / ${g.upMbps ?? "—"} Mbps` : "—"],
                    ["Fournisseur", g.ispName ? `${g.ispName}${g.ispAsn ? ` · ${g.ispAsn}` : ""}` : "—"],
                    ["Perte paquets", g.packetLossPercent != null ? `${g.packetLossPercent} %` : "—"],
                    ["Disponibilité", g.uptimePercent != null ? `${g.uptimePercent} %` : "—"],
                    ["Dernier contact", g.lastSeenAt ? timeAgo(g.lastSeenAt) : "jamais"],
                  ]} />
                </CompactRow>
              );
            })}
          </Section>
        </Column>

        {/* ── NAS ── */}
        <Column icon="bi-hdd-network" title="NAS" total={nas.length} counts={cNas} labels={["OK", "avert.", "crit."]}>
          <Section loading={loading} error={errors.monitoring} count={nasSorted.length} empty="Aucun NAS enregistré.">
            {nasSorted.map(n => {
              const st = deviceStatus(n);
              const { latest } = backupSummary(n.backupJobs);
              const pct = n.storagePercent;
              return (
                <CompactRow key={n.id} status={st} expandAll={expandAll} name={n.name}
                  sub={[n.companyName, latest ? `dern. sauvegarde ${timeAgoEpoch(latest.lastRunEpoch)}` : (n.backupJobs || []).length ? "sauvegardes : date inconnue" : "aucune sauvegarde"].filter(Boolean).join(" · ")}
                  side={pct != null ? <MiniBar pct={pct} color={storageColor(pct)} /> : "—"}
                  why={why("nas", n.id)}>
                  <Facts bordered={false} items={[
                    ["RAID", n.raidStatus || "—", n.raidStatus && n.raidStatus.toLowerCase() !== "normal" ? "var(--danger)" : undefined],
                    ["Température", n.temperatureC != null ? `${n.temperatureC} °C` : "—"],
                    ["Stockage", `${formatStorage(n.storageUsedGb)} / ${formatStorage(n.storageTotalGb)}`],
                    ["Synchro", timeAgo(n.lastSyncedAt)],
                  ]} />
                  <div style={{ marginTop: 6 }}>
                    {(n.backupJobs || []).length > 0 ? <><LastBackupLine jobs={n.backupJobs} /><BackupJobList jobs={n.backupJobs} /></>
                      : <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>Aucune tâche de sauvegarde détectée.</span>}
                  </div>
                </CompactRow>
              );
            })}
          </Section>
        </Column>

        {/* ── Serveurs ── */}
        <Column icon="bi-pc-display-horizontal" title="Serveurs" total={servers.length} counts={cSrv} labels={["OK", "avert.", "crit."]}>
          <Section loading={loading} error={errors.servers} count={srvSorted.length} empty="Aucun serveur supervisé.">
            {srvSorted.map(s => {
              const st = serverStatus(s);
              const online = s.status === "ONLINE";
              const d = diskInfo(s);
              const reason = !online ? `Hors ligne${d ? ` — disque ${d.pct} %` : ""}` : why("server", s.id);
              return (
                <CompactRow key={s.id} status={st} expandAll={expandAll} name={s.hostname || "—"}
                  sub={[s.companyName, online ? "en ligne" : "hors ligne"].filter(Boolean).join(" · ")}
                  side={!online ? <span style={{ color: "var(--danger)" }}>Hors ligne</span> : d ? <MiniBar pct={d.pct} color={d.color} /> : "—"}
                  why={reason}>
                  <Facts bordered={false} items={[
                    ["IP", s.ipAddress],
                    ["OS", s.os],
                    ["CPU", s.cpu],
                    ["RAM", s.ramGb ? `${s.ramGb} Go` : "—"],
                    ["Dernier reboot", s.lastRebootTime ? new Date(s.lastRebootTime).toLocaleDateString("fr-FR") : "—"],
                    ["Connecté", s.currentLoggedUser],
                    ["Agent", s.lastSeenAt ? timeAgo(s.lastSeenAt) : "jamais connecté", online ? undefined : "var(--danger)"],
                  ]} />
                </CompactRow>
              );
            })}
          </Section>
        </Column>
      </div>
    </div>
  );
}